// # Duration Stats Component
import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaClock, FaHourglassHalf, FaCheckCircle } from 'react-icons/fa';
import './chp_duartion_stats1.css';

// # Import chapter icons mapping from step1_chapter.js
import anatomyIcon from './step1_icons/anatomy.png';
import biochemistryIcon from './step1_icons/biochemistry.png';
import bloodIcon from './step1_icons/blood.png';
import cnsIcon from './step1_icons/cns.png';
import endocrineIcon from './step1_icons/endocrine.png';
import eyeIcon from './step1_icons/eye.png';
import gitIcon from './step1_icons/git.png';
import heartIcon from './step1_icons/heart.png';
import immunologyIcon from './step1_icons/immunology.png';
import infectiousIcon from './step1_icons/infectious.png';
import kidneyIcon from './step1_icons/kidney.png';
import lungsIcon from './step1_icons/lungs.png';
import musculoskeletalIcon from './step1_icons/musculoskeletal.png';
import pathologyIcon from './step1_icons/pathology.png';
import pharmaIcon from './step1_icons/pharma.png';
import psychiatryIcon from './step1_icons/psychiatry.png';
import publicHealthIcon from './step1_icons/public_health.png';
import reproductiveIcon from './step1_icons/reproductive-system.png';
import revisionIcon from './step1_icons/revision.png';
import skinIcon from './step1_icons/skin.png';

// # Chapter icons mapping
const chapterIcons = {
  biochemistry: biochemistryIcon,
  immunology: immunologyIcon,
  microbiology: infectiousIcon,
  general_pathology: pathologyIcon,
  pharmacology: pharmaIcon,
  biostatistics: publicHealthIcon,
  behavioral_science: psychiatryIcon,
  cardiovascular: heartIcon,
  endocrine: endocrineIcon,
  gastrointestinal: gitIcon,
  hematology_oncology: bloodIcon,
  musculoskeletal_and_connective_tissue: musculoskeletalIcon,
  dermatology: skinIcon,
  neurology: cnsIcon,
  special_senses: eyeIcon,
  psychiatry: psychiatryIcon,
  renal: kidneyIcon,
  reproductive: reproductiveIcon,
  respiratory: lungsIcon,
  rapid_review: revisionIcon
};

// # Helper function to calculate duration between two dates
const calculateDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return { days, hours, minutes };
};

// # Helper function to format duration
const formatDuration = (duration) => {
  if (!duration) return 'Not completed';

  const parts = [];
  if (duration.days > 0) parts.push(`${duration.days}d`);
  if (duration.hours > 0) parts.push(`${duration.hours}h`);
  if (duration.minutes > 0) parts.push(`${duration.minutes}m`);

  return parts.length > 0 ? parts.join(' ') : '< 1m';
};

function ChapterDurationStats({ libraryData }) {
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [chapterStats, setChapterStats] = useState([]);

  useEffect(() => {
    if (!libraryData?.step1) return;

    const stats = Object.entries(libraryData.step1)
      .map(([chapterId, chapterData]) => {
        // # Calculate chapter duration
        const chapterDuration = calculateDuration(
          chapterData.ChptStartedAt,
          chapterData.iscomplete_at
        );

        // # Calculate topic durations
        const topicStats = Object.entries(chapterData)
          .filter(([key]) => key !== 'iscomplete' && key !== 'iscomplete_at' && 
                            key !== 'ChptStartedAt' && key !== 'IsChapStarted')
          .map(([topicId, topicData]) => ({
            id: topicId,
            name: topicId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
            duration: calculateDuration(topicData.TopicStartedAt, topicData.completedAt),
            isComplete: topicData.completed,
            startDate: topicData.TopicStartedAt,
            endDate: topicData.completedAt
          }))
          .filter(topic => topic.startDate) // # Only show started topics
          .sort((a, b) => {
            if (!a.duration || !b.duration) return 0;
            const aDuration = (a.duration.days * 24 * 60) + (a.duration.hours * 60) + a.duration.minutes;
            const bDuration = (b.duration.days * 24 * 60) + (b.duration.hours * 60) + b.duration.minutes;
            return bDuration - aDuration;
          });

        return {
          id: chapterId,
          name: chapterId.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' '),
          icon: chapterIcons[chapterId],
          duration: chapterDuration,
          isComplete: chapterData.iscomplete,
          startDate: chapterData.ChptStartedAt,
          endDate: chapterData.iscomplete_at,
          topics: topicStats,
          totalTopics: topicStats.length,
          completedTopics: topicStats.filter(t => t.isComplete).length
        };
      })
      .filter(chapter => chapter.startDate && chapter.topics.length > 0) // # Only show chapters with started topics
      .sort((a, b) => {
        if (!a.duration || !b.duration) return 0;
        const aDuration = (a.duration.days * 24 * 60) + (a.duration.hours * 60) + a.duration.minutes;
        const bDuration = (b.duration.days * 24 * 60) + (b.duration.hours * 60) + b.duration.minutes;
        return bDuration - aDuration;
      });

    setChapterStats(stats);
  }, [libraryData]);

  const toggleChapter = (chapterId) => {
    setExpandedChapters(prev => {
      const newSet = new Set(prev);
      if (newSet.has(chapterId)) {
        newSet.delete(chapterId);
      } else {
        newSet.add(chapterId);
      }
      return newSet;
    });
  };

  return (
    <div className="chpdur1_container">
      <div className="chpdur1_header">
        <div className="chpdur1_header-icon">
          <FaHourglassHalf />
        </div>
        <p>Track how long it takes to complete each chapter and its topics</p>
      </div>

      <div className="chpdur1_stats-list">
        {chapterStats.map(chapter => (
          <div key={chapter.id} className="chpdur1_chapter-card">
            <div 
              className="chpdur1_chapter-header"
              onClick={() => toggleChapter(chapter.id)}
            >
              <div className="chpdur1_chapter-icon">
                <img src={chapter.icon} alt={chapter.name} />
                {chapter.isComplete && (
                  <div className="chpdur1_completion-badge">
                    <FaCheckCircle />
                  </div>
                )}
              </div>
              
              <div className="chpdur1_chapter-info">
                <div className="chpdur1_chapter-title">
                  <h3>{chapter.name}</h3>
                  {chapter.isComplete && (
                    <span className="chpdur1_completion-status">Completed</span>
                  )}
                </div>
                <div className="chpdur1_completion-info">
                  <FaClock className="chpdur1_clock-icon" />
                  <span>
                    {chapter.isComplete 
                      ? formatDuration(chapter.duration)
                      : <span className="chpdur1_in-progress">In Progress</span>
                    }
                  </span>
                </div>
              </div>

              <div className="chpdur1_expand-icon">
                {expandedChapters.has(chapter.id) ? <FaChevronUp /> : <FaChevronDown />}
              </div>
            </div>

            {expandedChapters.has(chapter.id) && (
              <div className="chpdur1_topic-list">
                {chapter.topics.map(topic => (
                  <div key={topic.id} className="chpdur1_topic-item">
                    <div className="chpdur1_topic-name">{topic.name}</div>
                    <div className="chpdur1_topic-duration">
                      {topic.isComplete ? (
                        <>
                          <FaClock className="chpdur1_clock-icon" />
                          <span>{formatDuration(topic.duration)}</span>
                        </>
                      ) : (
                        <span className="chpdur1_in-progress">In Progress</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default ChapterDurationStats;
